import React, { useState, useEffect } from 'react';
import PageHelmet from '../components/PageHelmet.jsx';
import {
    Form, Button, Alert, Modal, Dropdown, OverlayTrigger, Tooltip
} from 'react-bootstrap';
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
    FiFileText,
    FiUsers,
    FiLayout,
    FiBriefcase,
} from "react-icons/fi";
import { MdArrowBackIos } from "react-icons/md";
import '../styles/Subscriptions.css';
import CustomDataGrid from '../components/CustomDataGrid.jsx';
import {
    existingQuotaExtensionsGridColumns,
    existingQuotaExtensionsRowMapping,
    existingSubscriptionsGridColumns,
    subscriptionsGridOptionalColumns,
    existingSubscriptionsRowMapping,
    generateGridColumns,
    generateGridRows,
} from '../utils/PageUtils.js';
import { fetchUsersFromDB } from '../utils/api.js';
import api from './PlanMock.js';
import ActionSubscriptionModal from '../components/ActionSubscriptionModal.jsx';

const Subscriptions = ({ userRole, bearerToken }) => {

    const [users, setUsers] = useState([]);
    const [plans, setPlans] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [subscriptionFields, setSubscriptionFields] = useState({
        user: "",
        plan: "",
        billing_cycle: "",
        payment_status: "Pending"
    });
    const [quotaExtensionFields, setQuotaExtensionFields] = useState({
        subscription_id: "",
        user: "",
        quota: {
            campaigns: 0,
            contacts: 0,
            templates: 0,
            collaterals: 0,
            qrcodes: 0,
            trackables: 0,
            requests: 0,
        },
        price: 0,
        payment_status: "Pending"
    })
    const [selectedQuotaExtension, setSelectedQuotaExtension] = useState(null)
    const [selectedQuotaId, setSelectedQuotaId] = useState(null)
    const [showQuotaExtensionModal, setShowQuotaExtensionModal] = useState(false)

    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const handleMobileBack = () => {
        if (selectedQuotaExtension) {
            // when on quota detail screen, go back to quota list and clear selection
            setSelectedQuotaExtension(null);
            setSelectedQuotaId(null);
        } else {
            // when on subscription screen, close it
            handleCloseSubscriptionDetails();
        }
    }

    useEffect(() => {
        if (!isMobile) {
            document.body.style.overflow = "auto";
        }
    }, [isMobile]);


    const [subscriptionError, setSubscriptionError] = useState(null);



    const [selectedRowId, setSelectedRowId] = useState(null);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showActivateModal, setShowActivateModal] = useState(false);
    const [createSubscriptionInfo, setCreateSubscriptionInfo] = useState(null);
    const [createSubscriptionError, setCreateSubscriptionError] = useState(null);
    const [quotaExtensions, setQuotaExtensions] = useState([]);
    const [quotaExtensionsError, setQuotaExtensionsError] = useState(null);
    const featureFields = [
        { name: "campaigns", label: "Campaigns", icon: <FiLayout /> },
        { name: "contacts", label: "Contacts", icon: <FiUsers /> },
        { name: "templates", label: "Templates", icon: <FiFileText /> },
        { name: "collaterals", label: "Collaterals", icon: <FiBriefcase /> },
        { name: "qrcodes", label: "QR Codes", icon: <FiFileText /> },      // new
        { name: "trackables", label: "Trackables", icon: <FiFileText /> }, // new
        { name: "requests", label: "Requests", icon: <FiFileText /> },     // new
    ];
    const [showSuspendSubscriptionModal, setShowSuspendSubscriptionModal] = useState(false)
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showRenewModal, setShowRenewModal] = useState(null);


    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [upgradeCompleted, setUpgradeCompleted] = useState(false);
    const [showActivateModalForSuspended, setShowActivateModalForSuspended] = useState(false);
    const [showUpgradeReviewModal, setShowUpgradeReviewModal] = useState(false)
    const [showRenewReviewModal, setShowRenewReviewModal] = useState(false);
    const [freePlanName, setFreePlanName] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [usersResponse, plansResponse] = await Promise.all([
                    fetchUsersFromDB(bearerToken),
                    api.get('/subscription-plans')
                ]);

                console.log("Users response:", usersResponse);
                console.log("Plans response:", plansResponse);
                if (usersResponse.status === 200 && plansResponse.status === 200) {
                    const usersData = usersResponse.data;
                    const plansData = plansResponse.data;
                    console.log("Fetched users:", usersData);
                    console.log("Fetched plans:", plansData);
                    setUsers(usersData.users);
                    setPlans(plansData);
                    const freePlan = plansData.find(
                        p => p.monthly_price === 0 || p.annual_price === 0
                    );

                    if (freePlan) {
                        setFreePlanName(freePlan.name); // save the name dynamically
                    }
                } else {
                    console.error("Failed to fetch users or plans");
                    throw new Error("Failed to fetch data");
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        }
        fetchData();
    }, [])


    useEffect(() => {
        const fetchSubscriptions = async () => {
            try {
                const response = await api.get('/subscriptions')
                if (response.status === 200) {
                    const data = response.data;
                    console.log("Fetched subscriptions:", data);
                    setSubscriptions(data);
                }
            } catch (error) {
                console.error("Error fetching subscriptions:", error);
                setSubscriptionError("Failed to fetch subscriptions. Please try again later.");
            }
        }
        fetchSubscriptions();
    }, [])

    useEffect(() => {
        const fetchQuotaExtensions = async () => {
            try {
                const subscriptionId = selectedSubscription?.subscription_id;
                const response = await api.get(`/subscriptions/${subscriptionId}/quota-extensions`);
                console.log("Quota extensions response", response)
                if (response.status == 200) {
                    setQuotaExtensions(response.data)
                } else {
                    setQuotaExtensionsError("Error while fetching quota extensions")
                }
            } catch (e) {
                setQuotaExtensionsError("Error while fetching quota extensions")
            }
        }
        fetchQuotaExtensions();
    }, [selectedSubscription])

    const existingSubscriptionsRows = generateGridRows(subscriptions, existingSubscriptionsRowMapping);

    const exisitingSubscriptionsColumns = generateGridColumns(
        existingSubscriptionsGridColumns,
        "user",
        undefined,
        subscriptionsGridOptionalColumns
    )

    const quotaExtensionsForGrid = quotaExtensions.map((p) => ({
        ...p,
        price: `$${p.price.toLocaleString()}`,
        quota:
            typeof p.quota === "object" && p.quota !== null
                ? Object.entries(p.quota)
                    .map(([k, v]) => `${k}: ${v}`)
                    .join("\n")
                : p.quota,
        used_quota:
            typeof p.used_quota === "object" && p.used_quota !== null
                ? Object.entries(p.used_quota)
                    .map(([k, v]) => `${k}: ${v}`)
                    .join("\n")
                : p.used_quota

    }));
    const quotaExtensionRows = generateGridRows(quotaExtensionsForGrid, existingQuotaExtensionsRowMapping);
    const quotaExtensionColumns = generateGridColumns(
        existingQuotaExtensionsGridColumns,
        "quota_extension_id",
        "10px"
    );


    const handleRowClick = (e) => {
        console.log("Row clicked:", e);
        setSubscriptionError(null);
        if (selectedRowId === e.row.id) {
            setSelectedRowId(null);
            setSelectedSubscription(null);
            return;
        }
        setSelectedRowId(e.row.id);
        const subscription = subscriptions.find((sub) => sub.subscription_id === e.row.subscription_id);
        console.log("Selected subscription:", subscription);
        setSelectedSubscription(subscription);
        // Clear any selected quota extension when opening a subscription
        setSelectedQuotaExtension(null);

    }

    const handleCloseSubscriptionDetails = () => {
        console.log("Close subscription details");
        setSelectedSubscription(null)
        setSelectedRowId(null)
        // Also clear quota detail when closing subscription mobile panel
        setSelectedQuotaExtension(null)
        setSelectedQuotaId(null)
    }

    const handleOpenCreateModal = () => {
        console.log("Open create subscription modal");
        setShowCreateModal(true);
        setCreateSubscriptionError(null);
        setCreateSubscriptionInfo(null)
        setSubscriptionFields({
            user: "",
            plan: "",
            billing_cycle: "",
            payment_status: "Pending"
        })
    }

    const handleCreateSubscription = async (e, actionType) => {
        console.log("Create subscription with fields:", subscriptionFields);
        console.log("Event:", e);
        console.log("Action Type:", actionType);
        setCreateSubscriptionError(null);
        setCreateSubscriptionInfo(null);
        e.preventDefault();

        const { user, plan, billing_cycle, payment_status, customer_id, subscription_plan_id } = subscriptionFields;

        if (actionType === "active") {
            if (!user || !plan || !billing_cycle || !payment_status) {
                setCreateSubscriptionError("Please fill all required fields to activate the subscription.");
                return;
            }
            if (plan !== freePlanName && subscriptionFields.payment_status !== 'Paid') {
                setCreateSubscriptionError("You can activate subscription only when payment status is paid")
                return;
            }
            if (plan === freePlanName && billing_cycle !== 'Monthly') {
                setCreateSubscriptionError("Billing cycle should be Monthly for Free Plan subscription")
                return;
            }
        }

        if (actionType === "draft") {
            const isEmptyExceptPayment =
                (!user || user.trim() === "") &&
                (!plan || plan === "" || plan === "No change") &&
                (!billing_cycle || billing_cycle === "");

            if (isEmptyExceptPayment) {
                setCreateSubscriptionError(
                    "Please fill at least one field (User, Plan, or Billing Cycle) to save as draft."
                );
                return;
            }
        }

        const payload = {
            status: actionType,
            user,
            plan,
            billing_cycle,
            payment_status,
            subscription_id: crypto.randomUUID(),
            subscription_plan_id,
            customer_id,
        }
        console.log("Payload for creating subscription:", payload);
        try {

            const response = await api.post('/subscriptions', payload);
            console.log("Create subscription response:", response);
            if (response.status === 201) {
                console.log("Subscription created successfully:", response.data);
                setSubscriptions([...subscriptions, response.data]);
                setCreateSubscriptionInfo(`Subscription created successfully for ${user}`);
                setSubscriptionFields({
                    user: "",
                    plan: "",
                    billing_cycle: "",
                    payment_status: "Pending"
                })
                setTimeout(() => setShowCreateModal(false), 3000);

            } else {
                console.error("Failed to create subscription:", response);
                setCreateSubscriptionError("Failed to create subscription. Please try again later.");
            }

        } catch (error) {
            console.error("Error creating subscription:", error);
            setCreateSubscriptionError("Failed to create subscription. Please try again later.");
        }


    }

    const handleOpenActivateModal = async (subscription) => {
        console.log("Activating subscription", subscription)
        if (subscription.status === 'draft') {
            setShowActivateModal(true);

        } else if (subscription.status === 'Suspended') {
            setShowActivateModalForSuspended(true)
        }
        setCreateSubscriptionError(null);
        setCreateSubscriptionInfo(null);
        setSubscriptionFields({
            user: subscription.user,
            plan: subscription.plan,
            billing_cycle: subscription.billing_cycle,
            payment_status: subscription.payment_status,
            subscription_id: subscription.subscription_id,

        })
    }

    const handleCloseActivateModal = () => {
        setShowActivateModal(false);
        setCreateSubscriptionError(null);
        setCreateSubscriptionInfo(null);
        setSubscriptionFields({
            user: "",
            plan: "",
            billing_cycle: "",
            payment_status: "Pending"
        })
    }

    const handleActivateSubscription = async (e) => {
        e.preventDefault();
        setCreateSubscriptionError(null);
        setCreateSubscriptionInfo(null);
        console.log("Activating subscription", subscriptionFields)
        const { subscription_id, user, plan, billing_cycle, payment_status } = subscriptionFields;

        if (!user || !plan || !billing_cycle || !payment_status) {
            setCreateSubscriptionError("All fields are mandatory to activate subscription")
            return;
        }
        if (plan !== freePlanName && subscriptionFields.payment_status !== 'Paid') {
            setCreateSubscriptionError("You can activate subscription only when payment status is paid ")
            return;
        }
        if (plan === freePlanName && billing_cycle !== 'Monthly') {
            setCreateSubscriptionError("Billing cycle should be Monthly for Free Plan subscription")
            return;
        }

        const payload = {
            action: "activate",
            user,
            plan,
            billing_cycle,
            payment_status
        }
        try {
            const response = await api.post(`/subscriptions/${subscription_id}/actions`, payload);
            console.log("Response of activate user", response);
            if (response.status === 201) {
                setCreateSubscriptionInfo("Subscription Activated Successfully");
                setSubscriptions((prev) =>
                    prev.map((sub) =>
                        sub.subscription_id === subscription_id ? { ...sub, ...response.data } : sub
                    )
                );

                setSubscriptionFields({
                    user: "",
                    plan: "",
                    billing_cycle: "",
                    payment_status: "Pending"
                })
                setSelectedSubscription(response.data)
                setTimeout(() => setShowActivateModal(false), 3000)
            } else {
                setCreateSubscriptionError("Cannot activate subscription at this time")
            }
        } catch (error) {
            console.log("Error while activating subscription")
            setCreateSubscriptionError("Error while activating subscription")
        }

    }

    const handlePlanSelect = (planName, planID) => {
        if (planName === freePlanName) {
            setSubscriptionFields({
                ...subscriptionFields,
                plan: planName,
                payment_status: "NA",  // auto-set NA for Free
                billing_cycle: "Monthly",
                subscription_plan_id: planID
            });
        } else {
            setSubscriptionFields({
                ...subscriptionFields,
                plan: planName,
                subscription_plan_id: planID
                // Do not change payment_status for paid plans
            });
        }
    };

    const handleQuotaExtensionRowClick = (e) => {
        const rowId = e?.row?.id;
        if (selectedQuotaId === rowId) {
            setSelectedQuotaId(null)
            setSelectedQuotaExtension(null)
            return;
        }
        setSelectedQuotaId(rowId);
        const quotaExtension = quotaExtensions.find((quota) => quota.quota_extension_id === e.row.quota_extension_id);
        console.log("selected quota", quotaExtension)
        setSelectedQuotaExtension(quotaExtension)
        // On mobile, opening a quota extension should bring up the quota detail screen
    }

    const handleQuotaExtension = async (e) => {
        console.log("Quota extension clicked")
        e.preventDefault();
        setCreateSubscriptionError(null);
        setCreateSubscriptionInfo(null)
        console.log("Quota extension fields", quotaExtensionFields);
        const { user, subscription_id, price, payment_status, quota } = quotaExtensionFields;

        const activeQuotas = Object.entries(quota)
            .filter(([key, value]) => value > 0);

        if (activeQuotas.length === 0) {
            setCreateSubscriptionError("No quotas available. Quota values must be greater than 0.")
            return
        }

        if (!user || !subscription_id || price === "" || price === 0 || !payment_status) {
            setCreateSubscriptionError("Please update all required fields. Price must be greater than 0.");
            return;
        }

        if (quotaExtensionFields.payment_status !== 'Paid') {
            setCreateSubscriptionError("You can extend quota only when payment status is paid")
            return;
        }

        const activeQuotasObject = Object.fromEntries(activeQuotas);

        console.log("Active quotas", activeQuotas)
        const payload = {
            action: "extend_quota",
            subscription_id,
            user,
            payment_status,
            price,
            quota: activeQuotasObject,
            quota_extension_id: crypto.randomUUID(),
            status: "Active",
            created_timestamp: new Date().toISOString(),
        }
        try {
            const response = await api.post(`/subscriptions/${subscription_id}/actions`, payload)
            console.log("Response of quota extension creation", response);
            if (response.status === 201) {
                setQuotaExtensions([...quotaExtensions, response.data])
                setCreateSubscriptionInfo("Quota Extended Successfully")
                setTimeout(() => setShowQuotaExtensionModal(false), 3000)
            }
            else {
                setCreateSubscriptionError("Error while quota extensions")
            }
        } catch (error) {
            setCreateSubscriptionError("Error while quota extensions")
        }
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name in quotaExtensionFields.quota) {
            setQuotaExtensionFields((prev) => {
                const newQuota = { ...prev.quota, [name]: value };

                // Get the plan object
                const plan = plans.find(p => p.plan_id === selectedSubscription?.subscription_plan_id);
                const overagePriceCents = plan ? Number(plan.overage_price || 0) : 0;
                console.log("Overage price cents:", overagePriceCents);
                console.log("plan for quota extension:", plan);
                // Calculate price for collaterals
                const collateralsQty = Number(name === "collaterals" ? value : newQuota.collaterals || 0);
                const newPriceInDollar = (collateralsQty * overagePriceCents) / 100;

                return {
                    ...prev,
                    quota: newQuota,
                    price: newPriceInDollar, // store in cents
                };
            });
        } else {
            setQuotaExtensionFields((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };


    const handleOpenQuotaExtensionModal = async (subscription) => {
        console.log("Extend Quota clicked for subscription", subscription);
        setShowQuotaExtensionModal(true);
        setCreateSubscriptionError(null)
        setCreateSubscriptionInfo(null);
        setQuotaExtensionFields({
            user: subscription.user,
            subscription_id: subscription.subscription_id,
            quota: {
                campaigns: 0,
                contacts: 0,
                templates: 0,
                collaterals: 0,
                qrcodes: 0,
                trackables: 0,
                requests: 0,
            },
            price: 0,
            payment_status: "Pending",
        });
    }

    const handleCloseQuotaExtensionModal = async () => {
        setShowQuotaExtensionModal(false);
        setCreateSubscriptionError(null);
        setCreateSubscriptionInfo(null);
        setQuotaExtensionFields({
            user: "",
            subscription_id: "",
            quota: {
                campaigns: 0,
                contacts: 0,
                templates: 0,
                collaterals: 0,
                qrcodes: 0,
                trackables: 0,
                requests: 0,
            },
            price: 0,
            payment_status: "Pending"
        })
    }

    const handleOpenSuspendModal = async () => {
        setShowSuspendSubscriptionModal(true);
        setCreateSubscriptionError(null)
        setCreateSubscriptionInfo(null)
    }

    const handleSuspendSubscription = async (reason) => {
        console.log("Suspend subscription clicked", selectedSubscription)
        try {
            const { user, subscription_id, customer_id } = selectedSubscription;

            if (!user || !subscription_id || !customer_id || !reason) {
                return { success: false, message: "Required fields are missing" };
            }

            const payload = {
                action: "suspend",
                user,
                subscription_id,
                customer_id,
                reason
            };

            const response = await api.post(`/subscriptions/${subscription_id}/actions`, payload);

            console.log("Response of suspend subscription", response)

            if (response.status === 201) {
                setSubscriptions((prev) =>
                    prev.map((sub) =>
                        sub.subscription_id === subscription_id
                            ? { ...sub, ...response.data }
                            : sub
                    )
                );
                setSelectedSubscription(response.data)

                return { success: true, message: "Subscription suspended successfully" };
            }

            return { success: false, message: "Failed to suspend subscription" };

        } catch (error) {
            return { success: false, message: "Something went wrong" };
        }
    };

    const handleOpenCancelModal = async () => {
        console.log("Show cancel modal clicked")
        setShowCancelModal(true);
        setCreateSubscriptionError(null)
        setCreateSubscriptionInfo(null)
    }

    const handleCancelSubscription = async (reason) => {
        console.log("Cancel subscription clicked", selectedSubscription);
        try {
            const { user, subscription_id, customer_id } = selectedSubscription;

            if (!user || !subscription_id || !customer_id || !reason) {
                return { success: false, message: "Required fields are missing" };
            }

            const payload = {
                action: "cancel",
                user,
                subscription_id,
                customer_id,
                reason
            };

            const response = await api.post(`/subscriptions/${subscription_id}/actions`, payload);

            console.log("Response of suspend subscription", response)

            if (response.status === 201) {
                setSubscriptions((prev) =>
                    prev.map((sub) =>
                        sub.subscription_id === subscription_id
                            ? { ...sub, ...response.data }
                            : sub
                    )
                );
                setSelectedSubscription(response.data)

                return { success: true, message: "Subscription cancelled successfully" };
            }

            return { success: false, message: "Failed to cancel subscription" };

        } catch (error) {
            return { success: false, message: "Something went wrong" };
        }
    }

    const handleActivateForSuspend = async () => {
        console.log("Activate subscription clicked", selectedSubscription);
        try {
            const { user, subscription_id, customer_id } = selectedSubscription;

            if (!user || !subscription_id || !customer_id) {
                return { success: false, message: "Required fields are missing" };
            }

            const payload = {
                action: "activate",
                user,
                subscription_id,
                customer_id,
            };

            const response = await api.post(`/subscriptions/${subscription_id}/actions`, payload);

            console.log("Response of activate subscription", response)

            if (response.status === 201) {
                setSubscriptions((prev) =>
                    prev.map((sub) =>
                        sub.subscription_id === subscription_id
                            ? { ...sub, ...response.data }
                            : sub
                    )
                );
                setSelectedSubscription(response.data)

                return { success: true, message: "Subscription activated successfully" };
            }

            return { success: false, message: "Failed to activate subscription" };

        } catch (error) {
            return { success: false, message: "Something went wrong" };
        }
    }

    const handleCloseQuotaDetails = async () => {
        setSelectedQuotaExtension(null)
        setSelectedQuotaId(null)
    }

    const handleOpenRenewModal = async () => {
        setShowRenewModal(true);
        setCreateSubscriptionError(null)
        setCreateSubscriptionInfo(null)
        setSubscriptionFields({
            user: selectedSubscription.user,
            plan: "No change",
            billing_cycle: "no change",
            payment_status: "Pending",
            subscription_id: selectedSubscription.subscription_id
        })
    }
    const handlePrepareRenewReview = () => {
        setCreateSubscriptionError(null);
        setCreateSubscriptionInfo(null);
        if (subscriptionFields.plan !== freePlanName && subscriptionFields.payment_status !== "Paid") {
            setCreateSubscriptionError("You can renew subscription only when payment status is paid");
            return;
        }

        setShowRenewReviewModal(true);
    };

    const handleRenewSubscription = async () => {
        setCreateSubscriptionError(null)
        setCreateSubscriptionInfo(null)
        console.log("Renew subscription for ", subscriptionFields)
        const { user, plan, billing_cycle, payment_status, subscription_id, plan_change, update_billing_cycle } = subscriptionFields;

        console.log("Billing cycle changed", update_billing_cycle);


        try {
            const payload = {
                action: "renew",
                user,
                subscription_id,
                plan_change,
                update_billing_cycle,
                payment_status,
                ...(plan_change ? { plan } : {}),
                ...(update_billing_cycle ? { billing_cycle } : {}),
            };
            console.log("payload", payload)
            const response = await api.post(`/subscriptions/${subscription_id}/actions`, payload);
            console.log("Response of renew subscription", response)

            if (response.status === 201) {
                setSubscriptions((prev) =>
                    prev.map((sub) =>
                        sub.subscription_id === subscription_id
                            ? { ...sub, ...response.data }
                            : sub
                    )
                );
                setSelectedSubscription(response.data)
                setCreateSubscriptionInfo("Subscription renewed successfully")
                setTimeout(() => {
                    setShowRenewModal(false);
                    setShowRenewReviewModal(false);
                }, 3000)
            } else {
                setCreateSubscriptionError("Something went wrong while renewing subscription")
            }

        } catch (error) {
            setCreateSubscriptionError("Something went wrong while renewing subscription")
        }
    }

    const handleCloseRenewModal = async () => {
        setCreateSubscriptionError(null);
        setCreateSubscriptionInfo(null);
        setSubscriptionFields({
            user: "",
            plan: "",
            billing_cycle: "",
            payment_status: "Pending"
        })
        setShowRenewModal(false)
    }

    const handleOpenUpgradeModal = async () => {
        setShowUpgradeModal(true);
        setCreateSubscriptionError(null)
        setCreateSubscriptionInfo(null)
        setUpgradeCompleted(false);

        setSubscriptionFields({
            user: selectedSubscription.user,
            plan: "No change",
            billing_cycle: "No change",
            payment_status: "Pending",
            subscription_id: selectedSubscription.subscription_id
        })
    }

    const handleUpgradeConfirm = async () => {
        setCreateSubscriptionError(null);
        setCreateSubscriptionInfo(null);
        if (!subscriptionFields.upgrade && !subscriptionFields.update_billing_cycle) {
            setCreateSubscriptionError("Please select a plan or billing cycle to upgrade subscription.");
            return;
        }

        if (subscriptionFields.payment_status !== "Paid") {
            setCreateSubscriptionError("You can upgrade subscription only when payment status is paid");
            return;
        }

        // Open review modal
        setShowUpgradeReviewModal(true);
    }
    const handleUpgradeSubscription = async (e) => {  // 👈 Add event parameter
        e.preventDefault();  // 👈 Prevent form submission

        setCreateSubscriptionError(null);
        setCreateSubscriptionInfo(null);
        console.log("Subscription upgrade clicked", subscriptionFields);

        try {
            let { user, plan, billing_cycle, payment_status, subscription_id, update_billing_cycle, upgrade } = subscriptionFields;

            console.log("Upgrade", upgrade);
            console.log("update_billing_cycle", update_billing_cycle);

            const payload = {
                action: "upgrade",
                user,
                subscription_id,
                payment_status,
                upgrade,                  // always include
                update_billing_cycle,      // always include
                ...(upgrade ? { plan } : {}),                // include plan only if upgrading
                ...(update_billing_cycle ? { billing_cycle } : {}), // include billing_cycle only if changing
            };

            console.log("Payload for upgrade:", payload);

            const response = await api.post(`/subscriptions/${subscription_id}/actions`, payload);
            console.log("Response of upgrade subscription", response);

            if (response.status === 201) {
                setCreateSubscriptionInfo("Subscription upgraded successfully");

                const updatedSub = { ...selectedSubscription, ...response.data };
                setSelectedSubscription(updatedSub);

                // Update subscriptions list
                setSubscriptions((prev) =>
                    prev.map((sub) =>
                        sub.subscription_id === subscription_id ? updatedSub : sub
                    )
                );

                // 👇 FIX: Reset form to initial state
                setSubscriptionFields({
                    user: updatedSub.user,
                    plan: "",
                    billing_cycle: "",
                    payment_status: "Pending",
                    subscription_id: updatedSub.subscription_id
                });

                setUpgradeCompleted(true);

                setTimeout(() => {
                    setShowUpgradeReviewModal(false);
                    setShowUpgradeModal(false)
                }, 2000);

            } else {
                setCreateSubscriptionError("Cannot upgrade subscription at this time");
            }
        } catch (error) {
            console.error(error);
            setCreateSubscriptionError("Something went wrong while upgrading subscription");
        }
    };

    const handlePlanChange = (planName, plan_change) => {
        // Determine what to display in the plan field
        const displayPlan = plan_change ? planName : "No change";

        // Determine the effective plan for logic (used for checking FREE)
        const effectivePlan = plan_change ? planName : selectedSubscription?.plan;
        const isFree = effectivePlan === freePlanName;

        // Base update (your original logic)
        let updatedFields = {
            ...subscriptionFields,
            plan: displayPlan,
            plan_change: plan_change,
            payment_status: isFree ? "NA" : subscriptionFields.payment_status,
        };

        // ⭐ ADD: FREE PLAN LOGIC (without modifying your lines)
        // If effective plan is FREE → force billing_cycle = Monthly
        if (isFree) {
            const wasAnnual = selectedSubscription?.billing_cycle === "Annual";

            updatedFields.billing_cycle = "Monthly";
            updatedFields.update_billing_cycle = wasAnnual ? true : false;
        }

        // If plan is not FREE and user selects No change → restore original cycle
        else if (!plan_change) {
            updatedFields.billing_cycle = selectedSubscription?.billing_cycle;
            updatedFields.update_billing_cycle = false;
        }

        setSubscriptionFields(updatedFields);
    };

    const handleCloseUpgradeModal = async () => {
        setCreateSubscriptionError(null)
        setCreateSubscriptionInfo(null)
        setSubscriptionFields({
            user: "",
            plan: "",
            billing_cycle: "",
            payment_status: "Pending"
        })
        setShowUpgradeModal(false)
    }
    const isUpgradeAvailable = () => {
        if (!selectedSubscription || !plans) return false;

        // Find current plan in plans array
        const currentPlan = plans.find(p => p.name === selectedSubscription.plan);
        if (!currentPlan) return false;

        // Convert prices to numbers (remove $ if present)
        const currentPrice = Number((currentPlan.monthly_price || "0").toString().replace("$", ""));

        // Find highest plan by numeric monthly price
        const highestPlan = plans.reduce((acc, p) => {
            const price = Number((p.monthly_price || "0").toString().replace("$", ""));
            const accPrice = Number((acc.monthly_price || "0").toString().replace("$", ""));
            return price > accPrice ? p : acc;
        }, plans[0]);

        const highestPrice = Number((highestPlan.monthly_price || "0").toString().replace("$", ""));

        // Only allow upgrade if billing cycle is Monthly OR current plan price < highest price
        return selectedSubscription.billing_cycle === "Monthly" || currentPrice < highestPrice;
    };

    const renderSubscriptionDetails = (showCloseIcon = true) => (
        <div className="subscription-details-container">
            <div className="plan-details-header-container" >
                <div className="header-buttons">
                    {selectedSubscription?.status === 'draft' || selectedSubscription?.status === 'Suspended' ? (
                        <div className="action-buttons">
                            <Button className='btn-activate' onClick={() => handleOpenActivateModal(selectedSubscription)}>Activate</Button>
                        </div>
                    ) : null}
                    {selectedSubscription?.status === 'active' && (
                        <div className="action-buttons">
                            <Button className="btn-suspend" onClick={() => handleOpenSuspendModal(selectedSubscription)}>Suspend</Button>
                            <Button className="btn-extend-quota" onClick={() => handleOpenQuotaExtensionModal(selectedSubscription)}>Extend Quota</Button>
                            <Button className="btn-upgrade" onClick={() => handleOpenUpgradeModal()}>Upgrade</Button>
                        </div>
                    )}
                    {selectedSubscription?.status === 'Awaiting Renewal' && (
                        <div className="action-buttons">
                            <Button className='btn-renew' onClick={() => handleOpenRenewModal()}>Renew</Button>
                            <Button className='btn-cancel' onClick={() => handleOpenCancelModal(selectedSubscription)}>Cancel</Button>
                        </div>
                    )}
                </div>
                {showCloseIcon && (
                    <IconButton onClick={handleCloseSubscriptionDetails} size="small">
                        <CloseIcon />
                    </IconButton>
                )}
            </div>
            <div className="plan-details">
                <p className="plan-details-heading"> SUBSCRIPTION DETAILS</p>
                <div className="plan-content">
                    {[
                        { label: "Subscription ID", value: selectedSubscription.subscription_id },
                        { label: "User", value: selectedSubscription.user },
                        { label: "Subscription Plan", value: selectedSubscription.plan },
                        { label: "Billing Cycle", value: selectedSubscription.billing_cycle },
                        { label: "Status", value: selectedSubscription.status },
                        { label: "Start Date", value: selectedSubscription.start_date },
                        { label: "End Date", value: selectedSubscription.end_date },
                        { label: "Payment Status", value: selectedSubscription.payment_status },
                        { label: "Created", value: selectedSubscription.created_timestamp },
                        { label: "Updated", value: selectedSubscription.updated_timestamp }
                    ].map((field, idx) => (
                        <div className="plan-fields" key={idx}>
                            <p className="plan-field-label">{field.label}:</p>
                            <p className="plan-field-value">{field.value}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderQuotaDetails = (showCloseIcon = true) => (
        <div className="quota-details-container">
            {showCloseIcon && (
                <div className="plan-details-header" style={{ justifyContent: 'flex-end' }}>
                    <IconButton style={{ paddingRight: 0 }} onClick={handleCloseQuotaDetails} >
                        <CloseIcon />
                    </IconButton>
                </div>
            )}
            <div className="plan-details">
                <p className="plan-details-heading"> QUOTA EXTENSION DETAILS</p>
                <div className="plan-content">
                    {[
                        { label: "ID", value: selectedQuotaExtension.quota_extension_id },
                        { label: "Price", value: selectedQuotaExtension.price },
                        { label: "Status", value: selectedQuotaExtension.status },
                        { label: "Created", value: selectedQuotaExtension.created_timestamp },
                    ].map((field, idx) => (
                        <div className="plan-fields" key={idx}>
                            <p className="plan-field-label">{field.label}:</p>
                            <p className="plan-field-value">{field.value}</p>
                        </div>
                    ))}
                    <div className="plan-fields">
                        <p className="plan-field-label">Quota Extensions:</p>
                        <div style={{ textAlign: "left" }} className="plan-field-value">
                            {selectedQuotaExtension?.quota && Object.entries(selectedQuotaExtension.quota).map(([k, v]) => (
                                <div key={k}>{`${k}: ${v}`}</div>
                            ))}
                        </div>
                    </div>
                    <div className="plan-fields">
                        <p className="plan-field-label">Used Quota:</p>
                        <div style={{ textAlign: "left" }} className="plan-field-value">
                            {selectedQuotaExtension?.used_quota && Object.entries(selectedQuotaExtension.used_quota).map(([k, v]) => (
                                <div key={k}>{`${k}: ${v}`}</div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    if (userRole !== "admin") {
        return (
            <div className="user-error">
                <h1>404 - Page Not Found</h1>
                <p>The page you're looking for doesn't exist.</p>
            </div>
        );
    }
    return (
        <div>
            <PageHelmet
                title="Admin Subscriptions"
                description="Admin Subscriptions Page"
                url={`${process.env.REACT_APP_COGNITO_REDIRECT_URL}/subscribe`}
            />
            <div className={`subscriptions-wrapper ${selectedSubscription ? "has-selected-sub" : ""} ${selectedQuotaExtension ? "has-selected-quota" : ""}`}>
                <div className="subscriptions-page main-row">
                    <div className="left-section sub-list-section">
                        <h2 className="subscriptions-title">Subscriptions</h2>
                        <div className="existing-subscriptions-container">
                            <CustomDataGrid
                                rows={existingSubscriptionsRows}
                                columns={exisitingSubscriptionsColumns}
                                totalRows={existingSubscriptionsRows.length}
                                onRowClick={handleRowClick}
                                selectedRowId={selectedRowId}
                                toolbarProps={{
                                    createAction: {
                                        show: true,
                                        onClick: handleOpenCreateModal,
                                        label: "+ Create"
                                    }
                                }}
                            />

                        </div>
                        {subscriptionError && (
                            <Alert variant="danger" className="mt-3">
                                {subscriptionError}
                            </Alert>
                        )}

                    </div>

                    <div className="right-section sub-details-section">
                        {!selectedSubscription ? (
                            <div className="right-section-content">
                                <p className="info-heading">Select a Subscription</p>
                                <p className="info-text">
                                    Click any subscription row to view or manage details.
                                </p>
                            </div>
                        ) : (
                            !isMobile && renderSubscriptionDetails(true)
                        )}
                        {/* Mobile Overlay 1: Subscription Details + Quota Grid */}
                        {isMobile && selectedSubscription && !selectedQuotaExtension && (
                            <div className="subscription-mobile-screen mobile-open">
                                <div className="mobile-header">
                                    <IconButton size="small" style={{ color: '#fff' }} onClick={handleMobileBack}>
                                        <MdArrowBackIos />
                                    </IconButton>
                                    <div className="mobile-title">{selectedSubscription?.name || "Subscription"}</div>
                                </div>
                                <div className="mobile-body">
                                    {renderSubscriptionDetails(false)}

                                    {/* Quota Grid Inside Mobile Screen 1 */}
                                    {selectedSubscription && (
                                        <div className="mobile-quota-list-section">
                                            <h2 className='subscriptions-title' style={{ marginTop: '20px' }}>Quota Extensions</h2>
                                            <CustomDataGrid
                                                rows={quotaExtensionRows}
                                                columns={quotaExtensionColumns}
                                                totalRows={quotaExtensionRows.length}
                                                onRowClick={handleQuotaExtensionRowClick}
                                                selectedRowId={selectedQuotaId}
                                                getRowHeight={() => "auto"}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {/* Mobile Overlay 2: Quota Details */}
                        {isMobile && selectedQuotaExtension && (
                            <div className="quota-mobile-screen mobile-open">
                                <div className="mobile-header">
                                    <IconButton size="small" style={{ color: '#fff' }} onClick={handleMobileBack}>
                                        <MdArrowBackIos />
                                    </IconButton>
                                    <div className="mobile-title">Quota Details</div>
                                </div>
                                <div className="mobile-body">
                                    {renderQuotaDetails(false)}
                                </div>
                            </div>
                        )}
                    </div>

                </div>
                {!isMobile && selectedSubscription && (

                    <div className="subscriptions-page quota-row">

                        <div className="left-section quota-list-section">
                            <h2 className='subscriptions-title'>Quota Extensions</h2>
                            <CustomDataGrid
                                rows={quotaExtensionRows}
                                columns={quotaExtensionColumns}
                                totalRows={quotaExtensionRows.length}
                                onRowClick={handleQuotaExtensionRowClick}
                                selectedRowId={selectedQuotaId}
                                getRowHeight={() => "auto"}
                            />
                        </div>

                        <div className="right-section quota-details-section">
                            {selectedSubscription && !selectedQuotaExtension && (
                                <div className="right-section-content">
                                    <p className="info-heading">Select a Quota Extension</p>
                                    <p className="info-text">
                                        Click any quota extension row to view details.
                                    </p>
                                </div>
                            )}
                            {selectedSubscription && selectedQuotaExtension && renderQuotaDetails(true)}
                        </div>
                    </div>
                )}

            </div>

            {/*Modal to create subscription */}

            <Modal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                centered
                backdrop="static"
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>New Subscription</Modal.Title>
                </Modal.Header>
                <div className='divider'></div>
                <Modal.Body>
                    <Form onSubmit={handleCreateSubscription}>
                        {/* User Dropdown */}
                        <div>
                            <label className="form-label">Select User</label>
                            <Dropdown className="mb-3 custom-dropdown">
                                <Dropdown.Toggle variant="light" id="dropdown-user" className="d-flex justify-content-between align-items-center">
                                    <span>{subscriptionFields.user || "Select User"}</span>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {users.map((user) => (
                                        <Dropdown.Item
                                            key={user.email}
                                            onClick={() => setSubscriptionFields({ ...subscriptionFields, user: user.email, customer_id: user.customer_id })}
                                        >
                                            {user.name} - {user.email}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>

                        {/* Plan Dropdown */}
                        <div>
                            <label className="form-label">Select Plan</label>
                            <Dropdown className="mb-3 w-100 custom-dropdown">
                                <Dropdown.Toggle
                                    variant="light"
                                    id="dropdown-plan"
                                    className="d-flex justify-content-between align-items-center"
                                >
                                    <span>{subscriptionFields.plan || "Select Plan"}</span>
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    {plans.map((plan) => (
                                        <Dropdown.Item
                                            key={plan.name}
                                            onClick={() => handlePlanSelect(plan.name, plan.plan_id)}
                                        >
                                            {plan.name}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>

                        {/* Billing Cycle */}
                        <div>
                            <label className="form-label">Billing Cycle</label>
                            <Dropdown className="w-100 custom-dropdown">
                                <Dropdown.Toggle
                                    variant="light"
                                    id="dropdown-billing-cycle"
                                    className="d-flex justify-content-between align-items-center"
                                >
                                    <span>{subscriptionFields.billing_cycle || "Select Billing Cycle"}</span>
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    <Dropdown.Item
                                        onClick={() => setSubscriptionFields({ ...subscriptionFields, billing_cycle: "Monthly" })}
                                    >
                                        Monthly
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={() => setSubscriptionFields({ ...subscriptionFields, billing_cycle: "Annual" })}
                                        disabled={subscriptionFields.plan === freePlanName} // disable if Free
                                    >
                                        Annual
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                        {/* AMOUNT SECTION */}
                        <div className="amount-section-wrapper">
                            <div className="amount-section-box">
                                <span className="amount-label">Amount to Pay</span>
                                <span className="amount-value">
                                    {(() => {
                                        const planToUse = subscriptionFields.plan || selectedSubscription?.plan;
                                        const bcToUse = subscriptionFields.billing_cycle;

                                        // Only show amount if user has selected both plan AND billing cycle
                                        if (!planToUse || !bcToUse) return "$0";

                                        const selectedPlan = plans.find(plan => plan.name === planToUse);
                                        if (!selectedPlan) return "$0";

                                        return bcToUse === "Annual"
                                            ? `$${selectedPlan.annual_price}`
                                            : `$${selectedPlan.monthly_price}`;
                                    })()}
                                </span>
                            </div>
                        </div>


                        {/* Payment Status */}
                        <div className="payment-status-section">
                            <p>Payment Status</p>
                            <Form.Check
                                type="radio"
                                label="Paid"
                                name="paymentStatus"
                                id="payment-paid"
                                value="Paid"
                                disabled={subscriptionFields.plan === freePlanName} // disable if Free
                                checked={subscriptionFields.payment_status === "Paid"}
                                onChange={(e) => setSubscriptionFields({ ...subscriptionFields, payment_status: e.target.value })}
                            />{' '}
                            <Form.Check
                                type="radio"
                                label="Pending"
                                name="paymentStatus"
                                id="payment-pending"
                                value="Pending"
                                disabled={subscriptionFields.plan === freePlanName} // disable if Free
                                checked={subscriptionFields.payment_status === "Pending"}
                                onChange={(e) => setSubscriptionFields({ ...subscriptionFields, payment_status: e.target.value })}
                            />
                            <Form.Check
                                type="radio"
                                label="NA"
                                name="paymentStatus"
                                value="NA"
                                checked={subscriptionFields.payment_status === "NA"}
                                onChange={(e) =>
                                    setSubscriptionFields({
                                        ...subscriptionFields,
                                        payment_status: e.target.value
                                    })
                                }
                            />
                        </div>

                        {createSubscriptionError && <Alert variant="danger" className="mt-3">{createSubscriptionError}</Alert>}
                        {createSubscriptionInfo && <Alert variant="success" className="mt-3">{createSubscriptionInfo}</Alert>}

                        {/* Action Buttons */}
                        <div className="action-buttons">
                            <Button
                                style={{ width: "48%" }}
                                className='save-draft'
                                variant="secondary"
                                type="button"
                                onClick={(e) => handleCreateSubscription(e, "draft")}

                            >
                                Save As Draft</Button>

                            <Button
                                style={{ width: "48%" }}
                                variant="primary"
                                type="button"
                                className='normal-btn'
                                onClick={(e) => handleCreateSubscription(e, "active")}
                            >
                                Activate
                            </Button>
                        </div>

                        {/* Alerts */}

                    </Form>

                </Modal.Body>

            </Modal>

            {/*Modal to activate subscription*/}

            <Modal
                show={showActivateModal}
                onHide={() => setShowActivateModal(false)}
                centered
                backdrop="static"
                size="lg"
            >
                <Modal.Header>
                    <Modal.Title>Activate Subscription</Modal.Title>
                </Modal.Header>
                <div className='divider'></div>
                <Modal.Body>

                    <Form onSubmit={handleActivateSubscription}>

                        <div className="subscription-user-box">
                            <p className="mb-1"><strong>{selectedSubscription?.name}</strong></p>
                            <p className="mb-0">{selectedSubscription?.user}</p>
                        </div>

                        <div className="mb-3">
                            <label style={{ fontWeight: 600 }}>Select Plan</label>
                            {/* Plan Dropdown */}
                            <Dropdown className="mb-3 w-100 custom-dropdown">
                                <Dropdown.Toggle
                                    variant="light"
                                    id="dropdown-plan"
                                    className="d-flex justify-content-between align-items-center"
                                >
                                    <span>{subscriptionFields.plan || "Select Plan"}</span>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {plans.map((plan) => (
                                        <Dropdown.Item
                                            key={plan.name}
                                            onClick={() => handlePlanSelect(plan.name)}
                                        >
                                            {plan.name}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>

                        {/* Billing Cycle */}
                        <div className="mb-2">
                            <label className="form-label">Select Billing Cycle</label>
                            <Dropdown className="w-100 custom-dropdown">
                                <Dropdown.Toggle
                                    variant="light"
                                    id="dropdown-billing-cycle"
                                    className="d-flex justify-content-between align-items-center"
                                >
                                    <span>{subscriptionFields.billing_cycle || "Select Billing Cycle"}</span>
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    <Dropdown.Item
                                        onClick={() => setSubscriptionFields({ ...subscriptionFields, billing_cycle: "Monthly" })}
                                    >
                                        Monthly
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={() => setSubscriptionFields({ ...subscriptionFields, billing_cycle: "Annual" })}
                                        disabled={subscriptionFields.plan === freePlanName} // disable if Free
                                    >
                                        Annual
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>

                        {/* AMOUNT SECTION */}
                        <div className="amount-section-wrapper">
                            <div className="amount-section-box">
                                <span className="amount-label">Amount to Pay</span>
                                <span className="amount-value">
                                    {(() => {
                                        const planToUse = subscriptionFields.plan || selectedSubscription?.plan;
                                        const bcToUse = subscriptionFields.billing_cycle || selectedSubscription?.billing_cycle;

                                        if (!planToUse || !bcToUse) return "$0";

                                        const selectedPlan = plans.find(plan => plan.name === planToUse);
                                        if (!selectedPlan) return "$0";

                                        return bcToUse === "Annual"
                                            ? `$${selectedPlan.annual_price}`
                                            : `$${selectedPlan.monthly_price}`;
                                    })()}
                                </span>
                            </div>
                        </div>

                        {/* Payment Status */}
                        <div className="payment-status-section">
                            <p>Payment Status</p>
                            <Form.Check
                                type="radio"
                                label="Paid"
                                name="paymentStatus"
                                id="payment-paid"
                                value="Paid"
                                disabled={subscriptionFields.plan === freePlanName} // disable if Free
                                checked={subscriptionFields.payment_status === "Paid"}
                                onChange={(e) => setSubscriptionFields({ ...subscriptionFields, payment_status: e.target.value })}
                            />{' '}
                            <Form.Check
                                type="radio"
                                label="Pending"
                                name="paymentStatus"
                                id="payment-pending"
                                value="Pending"
                                disabled={subscriptionFields.plan === freePlanName} // disable if Free
                                checked={subscriptionFields.payment_status === "Pending"}
                                onChange={(e) => setSubscriptionFields({ ...subscriptionFields, payment_status: e.target.value })}
                            />
                            <Form.Check
                                type="radio"
                                label="NA"
                                name="paymentStatus"
                                value="NA"
                                checked={subscriptionFields.payment_status === "NA"}
                                onChange={(e) =>
                                    setSubscriptionFields({
                                        ...subscriptionFields,
                                        payment_status: e.target.value
                                    })
                                }
                            />
                        </div>

                        {createSubscriptionError && <Alert variant="danger" className="mt-3">{createSubscriptionError}</Alert>}
                        {createSubscriptionInfo && <Alert variant="success" className="mt-3">{createSubscriptionInfo}</Alert>}

                        <div className="renew-action-buttons">
                            <Button
                                style={{ width: "48%" }}
                                className='save-draft'
                                variant="secondary"
                                type="button"
                                onClick={() => handleCloseActivateModal()}

                            >
                                Cancel
                            </Button>

                            <Button
                                style={{ width: "48%" }}
                                variant="primary"
                                type="button"
                                className='normal-btn'
                                onClick={(e) => handleActivateSubscription(e)}
                            >
                                Activate
                            </Button>
                        </div>

                        {/* Alerts */}

                    </Form>

                </Modal.Body>

            </Modal>

            {/* Modal for quota extensions*/}

            <Modal
                show={showQuotaExtensionModal}
                onHide={() => setShowQuotaExtensionModal(false)}
                centered
                backdrop="static"
                size="lg"
            >
                <Modal.Header>
                    <Modal.Title>Extend Quota</Modal.Title>
                </Modal.Header>
                <div className='divider'></div>
                <Modal.Body>

                    <Form onSubmit={handleQuotaExtension}>
                        {/* Selected User (No dropdown) */}
                        <div className="subscription-user-box">
                            <p className="mb-1"><strong>{selectedSubscription?.name}</strong></p>
                            <p className="mb-0">{selectedSubscription?.user}</p>
                        </div>
                        <div className="subscription-features-box">
                            <h5>Quota</h5>
                            <div className="features-grid">
                                {featureFields.map((feature) => (
                                    <div key={feature.name} className="feature-item">
                                        <div className="feature-label">
                                            {feature.icon}
                                            {feature.label}
                                        </div>
                                        <Form.Control
                                            type="number"
                                            name={feature.name}
                                            value={quotaExtensionFields.quota[feature.name] || ""}
                                            placeholder="0"
                                            onChange={handleInputChange}
                                            className="feature-input"
                                            required
                                            min={0}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div style={{
                            display: "flex",
                            justifyContent: "center",
                            marginTop: "16px"
                        }}>
                            <div className="amount-calculation-box" style={{
                                border: "1px solid #ddd",
                                borderRadius: "8px",
                                padding: "16px",
                                backgroundColor: "aliceblue",
                                maxWidth: "300px",
                                width: "100%",
                                textAlign: "left"
                            }}>
                                {(() => {
                                    const plan = plans.find(p => p.plan_id === selectedSubscription?.subscription_plan_id);
                                    const overagePriceCents = plan ? Number(plan.overage_price || 0) : 0;
                                    const collateralsQty = Number(quotaExtensionFields.quota.collaterals || 0);
                                    const totalDollars = (collateralsQty * overagePriceCents) / 100;

                                    return (
                                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                                <span>Collaterals Qty:</span>
                                                <span>{collateralsQty}</span>
                                            </div>
                                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                                <span>Price per Collateral:</span>
                                                <span>${(overagePriceCents / 100).toFixed(2)}</span>
                                            </div>
                                            <div style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                fontWeight: "bold",
                                                borderTop: "1px solid #ccc",
                                                paddingTop: "8px"
                                            }}>
                                                <span>Total:</span>
                                                <span>${totalDollars.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* Payment Status */}
                        <div className="payment-status-section">
                            <p>Payment Status</p>
                            <Form.Check
                                type="radio"
                                label="Paid"
                                name="paymentStatus"
                                id="payment-paid"
                                value="Paid"
                                checked={quotaExtensionFields.payment_status === "Paid"}
                                onChange={(e) => setQuotaExtensionFields({ ...quotaExtensionFields, payment_status: e.target.value })}
                            />{' '}
                            <Form.Check
                                type="radio"
                                label="Pending"
                                name="paymentStatus"
                                id="payment-pending"
                                value="Pending"
                                checked={quotaExtensionFields.payment_status === "Pending"}
                                onChange={(e) => setQuotaExtensionFields({ ...quotaExtensionFields, payment_status: e.target.value })}
                            />
                        </div>

                        {createSubscriptionError && <Alert variant="danger" className="mt-3">{createSubscriptionError}</Alert>}
                        {createSubscriptionInfo && <Alert variant="success" className="mt-3">{createSubscriptionInfo}</Alert>}

                        {/* Action Buttons */}
                        <div className="action-buttons">
                            <Button
                                style={{ width: "48%" }}
                                className='save-draft'
                                variant="secondary"
                                type="button"
                                onClick={() => handleCloseQuotaExtensionModal()}
                            >
                                Cancel
                            </Button>

                            <Button
                                style={{ width: "48%" }}
                                variant="primary"
                                type="button"
                                className='normal-btn'
                                onClick={(e) => handleQuotaExtension(e)}
                            >
                                Extend Quota
                            </Button>
                        </div>

                        {/* Alerts */}

                    </Form>

                </Modal.Body>

            </Modal>

            {/* Modal for suspend subscription*/}

            <ActionSubscriptionModal
                show={showSuspendSubscriptionModal}
                onClose={() => setShowSuspendSubscriptionModal(false)}
                actionType="suspend"
                actionTitle="Suspend Subscription"
                actionDescription="The subscription will be temporarily suspended."
                actionButtonLabel="Suspend"
                confirmMessage="Are you sure you want to suspend this subscription?"
                onSubmit={handleSuspendSubscription}
            />
            {/* Modal for cancel subscription*/}

            <ActionSubscriptionModal
                show={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                actionType="cancel"
                actionTitle="Cancel Subscription"
                actionDescription="This action is irreversible and will permanently deactivate the subscription."
                actionButtonLabel="Cancel"
                confirmMessage="Are you sure you want to cancel this subscription permanently?"
                onSubmit={handleCancelSubscription}
            />

            {/* Modal for activate subscription*/}

            <ActionSubscriptionModal
                show={showActivateModalForSuspended}
                onClose={() => setShowActivateModalForSuspended(false)}
                actionType="activate"
                actionTitle="Activate Subscription"
                actionDescription="This will reactivate the suspended subscription."
                actionButtonLabel="Activate"
                onSubmit={handleActivateForSuspend}
                buttonColor="#2e7d32"
                confirmButtonColor="#2e7d32"
                showReasonField={false}   // hides reason textarea
            />

            {/* Modal for renew subscription*/}

            <Modal
                show={showRenewModal}
                onHide={() => setShowRenewModal(false)}
                centered
                backdrop="static"
                size="lg"
            >
                <Modal.Header>
                    <Modal.Title>Renew Subscription</Modal.Title>
                </Modal.Header>

                <div className="divider"></div>

                <Modal.Body>
                    <div className="user-info-section">
                        <div className="user-details">
                            <div className="user-name">
                                <strong>{selectedSubscription?.name}</strong>
                            </div>
                            <div className="user-email">{selectedSubscription?.user}</div>
                        </div>

                        <div className="current-plan-badge">
                            <span className="badge-label">CURRENT</span>
                            <span className="badge-plan">
                                {selectedSubscription?.plan} • {selectedSubscription?.billing_cycle}
                            </span>
                        </div>
                    </div>

                    <Form onSubmit={handleRenewSubscription}>

                        {/* PLAN CHANGE SECTION (Left aligned, full-width dropdown) */}
                        <div
                            className="p-3 rounded mb-4"
                            style={{
                                background: "#f0f4ff", // soft pastel blue
                                border: "1px solid #c2d1ff",
                            }}
                        >
                            <p className="fw-semibold mb-2">Change Plan</p>

                            <Dropdown className="w-100 custom-dropdown">
                                <Dropdown.Toggle
                                    variant="light"
                                    className="d-flex justify-content-between"
                                    style={{ minHeight: "38px" }}
                                >
                                    <span>{subscriptionFields.plan || "Select Plan"}</span>
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    {/* No change option */}
                                    <Dropdown.Item
                                        onClick={() => handlePlanChange("No change", false)}

                                    >
                                        No change
                                    </Dropdown.Item>

                                    <Dropdown.Divider />

                                    {/* All plans except current */}
                                    {plans
                                        .filter(plan => plan.name !== selectedSubscription?.plan)
                                        .map(plan => (
                                            <Dropdown.Item
                                                key={plan.name}
                                                onClick={() => handlePlanChange(plan.name, true)}

                                            >
                                                {plan.name}
                                            </Dropdown.Item>
                                        ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>

                        {/* BILLING CYCLE SECTION (Centered in modal) */}
                        <div
                            className="p-3 rounded mb-4"
                            style={{
                                background: 'rgba(255, 77, 79, 0.1)',
                                border: "1px solid #ffcc99",
                            }}
                        >
                            <p className="fw-semibold mb-2">Update Billing Cycle</p>

                            {subscriptionFields.plan === freePlanName ||
                                (selectedSubscription?.plan === freePlanName && !subscriptionFields.plan_change) ? (
                                <p className="mb-0 text-center text-muted">
                                    Billing cycle cannot be upgraded for Free plans.
                                </p>
                            ) : (
                                <Dropdown className="w-100 custom-dropdown">
                                    <Dropdown.Toggle
                                        variant="light"
                                        className="d-flex justify-content-between"
                                        style={{ minHeight: "38px" }}
                                    >
                                        {subscriptionFields.billing_cycle || "Select Billing Cycle"}
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu>
                                        {/* No change */}
                                        <Dropdown.Item
                                            active={subscriptionFields.billing_cycle === "No change"}
                                            onClick={() =>
                                                setSubscriptionFields({
                                                    ...subscriptionFields,
                                                    billing_cycle: "No change",
                                                    update_billing_cycle: false,
                                                })
                                            }
                                        >
                                            No change
                                        </Dropdown.Item>

                                        <Dropdown.Divider />

                                        {/* Monthly */}
                                        <Dropdown.Item
                                            active={subscriptionFields.billing_cycle === "Monthly"}
                                            onClick={() =>
                                                setSubscriptionFields({
                                                    ...subscriptionFields,
                                                    billing_cycle: "Monthly",
                                                    update_billing_cycle: true,
                                                })
                                            }
                                            disabled={
                                                (selectedSubscription?.plan === freePlanName &&
                                                    subscriptionFields.plan === "No change") ||
                                                selectedSubscription?.billing_cycle === "Monthly"
                                            }
                                        >
                                            Monthly
                                        </Dropdown.Item>

                                        {/* Annual */}
                                        <Dropdown.Item
                                            active={subscriptionFields.billing_cycle === "Annual"}
                                            onClick={() =>
                                                setSubscriptionFields({
                                                    ...subscriptionFields,
                                                    billing_cycle: "Annual",
                                                    update_billing_cycle: true,
                                                })
                                            }
                                            disabled={
                                                (selectedSubscription?.plan === freePlanName &&
                                                    subscriptionFields.plan === "No change") ||
                                                subscriptionFields?.plan === freePlanName ||
                                                selectedSubscription?.billing_cycle === "Annual"
                                            }
                                        >
                                            Annual
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            )}

                            {/* Note */}
                            {((selectedSubscription?.plan === freePlanName &&
                                subscriptionFields.plan === "No change") ||
                                selectedSubscription?.billing_cycle === "Annual") && (
                                    <p className="mt-2 mb-0 text-muted text-center" style={{ fontSize: "0.875rem" }}>
                                        Billing cycle cannot be upgraded for Free plans or if Annual is already selected.
                                    </p>
                                )}
                        </div>


                        {/* AMOUNT SECTION */}
                        <div className="amount-section-wrapper">
                            <div className="amount-section-box">
                                <span className="amount-label">Amount to Pay</span>
                                <span className="amount-value">
                                    {(() => {
                                        // Use new plan if changed, otherwise current plan
                                        const planToUse = subscriptionFields.plan_change
                                            ? subscriptionFields.plan
                                            : selectedSubscription?.plan;

                                        // Use new billing cycle if changed, otherwise current cycle
                                        const bcToUse = subscriptionFields.update_billing_cycle
                                            ? subscriptionFields.billing_cycle
                                            : selectedSubscription?.billing_cycle;

                                        if (!planToUse || !bcToUse) return "$0";

                                        const selectedPlan = plans.find(plan => plan.name === planToUse);
                                        if (!selectedPlan) return "$0";

                                        return bcToUse === "Annual"
                                            ? `$${selectedPlan.annual_price}`
                                            : `$${selectedPlan.monthly_price}`;
                                    })()}
                                </span>
                            </div>
                        </div>

                        {/* PAYMENT STATUS */}
                        <div className="payment-box payment-status-row mb-4">
                            <p className="payment-status-label">Payment Status</p>
                            <div className="payment-status-options">
                                {/* NA option */}

                                {/* Paid option */}
                                <Form.Check
                                    type="radio"
                                    label="Paid"
                                    name="paymentStatus"
                                    value="Paid"
                                    checked={subscriptionFields.payment_status === "Paid"}
                                    onChange={(e) =>
                                        setSubscriptionFields({ ...subscriptionFields, payment_status: e.target.value })
                                    }
                                    disabled={subscriptionFields.plan === freePlanName || subscriptionFields.plan === "No change" && selectedSubscription?.plan === freePlanName}
                                />

                                {/* Pending option */}
                                <Form.Check
                                    type="radio"
                                    label="Pending"
                                    name="paymentStatus"
                                    value="Pending"
                                    checked={subscriptionFields.payment_status === "Pending"}
                                    onChange={(e) =>
                                        setSubscriptionFields({ ...subscriptionFields, payment_status: e.target.value })
                                    }
                                    disabled={subscriptionFields.plan === freePlanName || subscriptionFields.plan === "No change" && selectedSubscription?.plan === freePlanName}
                                />
                                <Form.Check
                                    type="radio"
                                    label="NA"
                                    name="paymentStatus"
                                    value="NA"
                                    checked={subscriptionFields.payment_status === "NA"}
                                    onChange={(e) =>
                                        setSubscriptionFields({ ...subscriptionFields, payment_status: e.target.value })
                                    }
                                />
                            </div>
                        </div>

                        {selectedSubscription?.plan !== freePlanName && selectedSubscription?.billing_cycle === "Annual" && (
                            <div className="subscription-note mb-3">
                                <strong>Note:</strong> You can downgrade the plan or change the billing cycle during renewal; once the subscription is active, you must wait until the next renewal to make changes.
                            </div>
                        )}

                        {createSubscriptionError && <Alert variant="danger">{createSubscriptionError}</Alert>}

                        {/* ACTION BUTTONS */}
                        <div className="renew-action-buttons">
                            <Button
                                style={{ width: "48%" }}
                                variant="secondary"
                                type="button"
                                onClick={() => handleCloseRenewModal()}
                            >
                                Cancel
                            </Button>

                            <Button
                                style={{ width: "48%" }}
                                variant="primary"
                                type="button"
                                onClick={() => handlePrepareRenewReview()}
                            >
                                Renew
                            </Button>
                        </div>

                    </Form>

                </Modal.Body>
            </Modal>

            {/* Modal for Upgrade subscription*/}

            <Modal
                show={showUpgradeModal}
                onHide={() => setShowUpgradeModal(false)}
                centered
                backdrop="static"
                size="lg"
            >
                <Modal.Header>
                    <Modal.Title>Upgrade Subscription</Modal.Title>
                </Modal.Header>

                <div className="divider"></div>

                <Modal.Body>
                    {/* USER INFO  and Current Subscription SECTION */}
                    <div className="user-info-section">
                        <div className="user-details">
                            <div className="user-name">
                                <strong>{selectedSubscription?.name}</strong>
                            </div>
                            <div className="user-email">{selectedSubscription?.user}</div>
                        </div>

                        <div className="current-plan-badge">
                            <span className="badge-label">CURRENT</span>
                            <span className="badge-plan">
                                {selectedSubscription?.plan} • {selectedSubscription?.billing_cycle}
                            </span>
                        </div>
                    </div>
                    {/* IF NOTHING TO UPGRADE */}
                    {!isUpgradeAvailable() && !upgradeCompleted ? (
                        <div className="text-center my-4">
                            <p style={{ color: "#0d6efd", fontWeight: 500, fontSize: "1rem", marginBottom: "20px" }}>
                                No upgrade available. You are already on the highest plan and annual billing cycle.
                            </p>
                            <Button variant="primary" onClick={() => setShowUpgradeModal(false)}>
                                OK
                            </Button>
                        </div>
                    ) : (

                        <Form onSubmit={handleUpgradeSubscription}>

                            {/* PLAN CHANGE SECTION (Left aligned, full-width dropdown) */}
                            <div
                                className="p-3 rounded mb-4"
                                style={{
                                    background: "#f0f4ff",
                                    border: "1px solid #c2d1ff",
                                }}
                            >
                                <p className="fw-semibold mb-2">Upgrade Plan</p>

                                <Dropdown className="w-100 custom-dropdown">
                                    <Dropdown.Toggle
                                        variant="light"
                                        className="d-flex justify-content-between"
                                        style={{ minHeight: "38px" }}
                                    >
                                        <span>{subscriptionFields.plan || "Select Plan"}</span>
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu>
                                        {/* No change option */}
                                        <Dropdown.Item
                                            onClick={() => {
                                                setSubscriptionFields(prev => ({
                                                    ...prev,
                                                    plan: "No change",
                                                    upgrade: false,
                                                    // KEEP the existing billing_cycle
                                                    // do NOT reset to Monthly
                                                }));
                                            }}
                                        >
                                            No change
                                        </Dropdown.Item>

                                        <Dropdown.Divider />

                                        {/* All plans except current */}
                                        {plans
                                            .filter(plan => {
                                                const currentPlan = plans.find(p => p.name === selectedSubscription?.plan);
                                                return plan.name !== selectedSubscription?.plan && plan.monthly_price > (currentPlan?.monthly_price || 0);
                                            })
                                            .map(plan => (
                                                <Dropdown.Item
                                                    key={plan.name}
                                                    onClick={() => {
                                                        setSubscriptionFields(prev => ({
                                                            ...prev,
                                                            plan: plan.name,
                                                            upgrade: true,
                                                            // Optional: keep billing cycle or reset only if needed
                                                        }));
                                                    }}
                                                >
                                                    {plan.name}
                                                </Dropdown.Item>
                                            ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>


                            {/* BILLING CYCLE SECTION */}
                            <div
                                className="p-3 rounded mb-4"
                                style={{
                                    background: 'rgba(255, 77, 79, 0.1)', // soft pastel blue
                                    border: "1px solid #c2d1ff",
                                }}
                            >
                                <p className="fw-semibold mb-2">Update Billing Cycle</p>

                                <Dropdown className="w-100 custom-dropdown">
                                    <Dropdown.Toggle
                                        variant="light"
                                        className="d-flex justify-content-between"
                                        style={{ minHeight: "38px" }}
                                    >
                                        {/* The display remains based on subscriptionFields.billing_cycle */}
                                        <span>{subscriptionFields.billing_cycle || "Select Billing Cycle"}</span>
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu>
                                        {/* No change option */}
                                        <Dropdown.Item
                                            onClick={() =>
                                                setSubscriptionFields({
                                                    ...subscriptionFields,
                                                    billing_cycle: "No change",
                                                    update_billing_cycle: false,
                                                })
                                            }
                                        >
                                            No change
                                        </Dropdown.Item>

                                        <Dropdown.Divider />

                                        {/* Monthly */}
                                        <Dropdown.Item
                                            disabled={true}
                                        >
                                            Monthly
                                        </Dropdown.Item>

                                        {/* Annual */}
                                        <Dropdown.Item
                                            onClick={() =>
                                                setSubscriptionFields({
                                                    ...subscriptionFields,
                                                    billing_cycle: "Annual",
                                                    update_billing_cycle: true,
                                                })
                                            }
                                            disabled={
                                                // 1. Disable if on FREE plan and no upgrade is selected
                                                (selectedSubscription?.plan === freePlanName && subscriptionFields.plan === "No change") ||
                                                // 2. Disable if already Annual (no change to be made)
                                                selectedSubscription?.billing_cycle === "Annual"

                                            }
                                        >
                                            Annual
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>


                            {/* AMOUNT SECTION */}
                            <div style={{
                                display: "flex",
                                justifyContent: "center",
                                margin: "10px 0px 20px"
                            }}>
                                <div className="amount-calculation-box" style={{
                                    border: "1px solid #ddd",
                                    borderRadius: "8px",
                                    padding: "16px",
                                    backgroundColor: 'rgb(240, 248, 255)',
                                    maxWidth: "340px",
                                    width: "100%",
                                    textAlign: "left"
                                }}>
                                    {(() => {
                                        const currentPlanName = selectedSubscription?.plan;
                                        const currentBilling = selectedSubscription?.billing_cycle;

                                        const userSelectedPlan =
                                            subscriptionFields.plan && subscriptionFields.plan !== "No change" && subscriptionFields.plan !== "";

                                        // billingToUse = if user selected a billing cycle in the form use it, else currentBilling
                                        const billingToUse = subscriptionFields.billing_cycle || currentBilling;

                                        const currentPlan = plans.find(p => p.name === currentPlanName);

                                        // selectedPlan only if user explicitly selected a different plan
                                        const selectedPlan = userSelectedPlan
                                            ? plans.find(p => p.name === subscriptionFields.plan)
                                            : null;

                                        // Compute current price (based on the current subscription billing cycle)
                                        const currentPrice = currentPlan
                                            ? (currentBilling === "Annual" ? Number(currentPlan.annual_price || 0) : Number(currentPlan.monthly_price || 0))
                                            : 0;

                                        let selectedPrice;

                                        if (selectedPlan) {
                                            // Case: user selected a different plan -> use that plan price with billingToUse
                                            selectedPrice = billingToUse === "Annual"
                                                ? Number(selectedPlan.annual_price || 0)
                                                : Number(selectedPlan.monthly_price || 0);
                                        } else if (subscriptionFields.billing_cycle && subscriptionFields.billing_cycle !== currentBilling) {
                                            // Case: billing cycle changed but plan not changed -> use current plan price with new billing cycle
                                            selectedPrice = billingToUse === "Annual"
                                                ? Number(currentPlan?.annual_price || 0)
                                                : Number(currentPlan?.monthly_price || 0);
                                        } else {
                                            // No plan selected and no billing-cycle change -> treat selected price as 0
                                            selectedPrice = 0;
                                        }

                                        const amountToPay = selectedPrice - currentPrice;
                                        const finalAmount = amountToPay > 0 ? amountToPay : 0;

                                        // Format function
                                        const fmt = (n) => `$${Number(n ?? 0).toFixed(2)}`;

                                        return (
                                            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                                <div style={{ display: "flex", justifyContent: "space-between", color: "#555" }}>
                                                    <span>Current Plan ({currentBilling || "-"})</span>
                                                    <span>{fmt(currentPrice)}</span>
                                                </div>

                                                <div style={{ display: "flex", justifyContent: "space-between", color: "#555" }}>
                                                    <span>
                                                        {selectedPlan ? `Selected Plan (${billingToUse})` :
                                                            (subscriptionFields.billing_cycle && subscriptionFields.billing_cycle !== currentBilling)
                                                                ? `Same Plan (${billingToUse})`
                                                                : "Selected Plan"}
                                                    </span>
                                                    <span>{fmt(selectedPrice)}</span>
                                                </div>

                                                <div style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    fontWeight: "700",
                                                    borderTop: "1px solid #e0e0e0",
                                                    paddingTop: "10px",
                                                    marginTop: "6px"
                                                }}>
                                                    <span>Total:</span>
                                                    <span>{fmt(finalAmount)}</span>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>

                            {/* PAYMENT STATUS */}
                            <div className="payment-box payment-status-row mb-2">
                                <p className="payment-status-label">Payment Status</p>
                                <div className="payment-status-options">
                                    <Form.Check
                                        type="radio"
                                        label="Paid"
                                        name="paymentStatus"
                                        value="Paid"
                                        checked={subscriptionFields.payment_status === "Paid"}
                                        onChange={(e) =>
                                            setSubscriptionFields({ ...subscriptionFields, payment_status: e.target.value })
                                        }
                                    />

                                    <Form.Check
                                        type="radio"
                                        label="Pending"
                                        name="paymentStatus"
                                        value="Pending"
                                        checked={subscriptionFields.payment_status === "Pending"}
                                        onChange={(e) =>
                                            setSubscriptionFields({ ...subscriptionFields, payment_status: e.target.value })
                                        }
                                    />
                                </div>
                            </div>
                            {createSubscriptionError && <Alert variant="danger">{createSubscriptionError}</Alert>}


                            {/* ACTION BUTTONS */}
                            <div className="renew-action-buttons">
                                <Button
                                    style={{ width: "48%" }}
                                    variant="secondary"
                                    type="button"
                                    onClick={() => handleCloseUpgradeModal()}
                                >
                                    Cancel
                                </Button>

                                <Button
                                    style={{ width: "48%" }}
                                    variant="primary"
                                    type="button"
                                    onClick={() => handleUpgradeConfirm()}
                                >
                                    Upgrade
                                </Button>
                            </div>

                        </Form>

                    )}

                </Modal.Body>
            </Modal>

            <Modal
                show={showUpgradeReviewModal}
                onHide={() => setShowUpgradeReviewModal(false)}
                centered
                backdrop="static"
                size="md"
            >
                <Modal.Header>
                    <Modal.Title>Confirm Upgrade</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>You're about to upgrade your subscription with the following changes:</p>

                    <div className="p-3 mb-3" style={{ background: "#f8f9fa", borderRadius: "5px" }}>
                        <p>
                            <strong>Plan:</strong> {subscriptionFields.upgrade ? subscriptionFields.plan : "No change"}
                        </p>
                        <p>
                            <strong>Billing Cycle:</strong> {subscriptionFields.update_billing_cycle ? subscriptionFields.billing_cycle : "No change"}
                        </p>
                    </div>

                    {subscriptionFields.upgrade && !subscriptionFields.update_billing_cycle && (
                        <p style={{ fontStyle: "italic", color: "#6c757d" }}>
                            You have changed only the plan and not the billing cycle. Do you want to proceed with renewal?
                        </p>
                    )}

                    {subscriptionFields.update_billing_cycle && !subscriptionFields.upgrade && (
                        <p style={{ fontStyle: "italic", color: "#6c757d" }}>
                            You have changed only the billing cycle and not the plan. Do you want to proceed with renewal?
                        </p>
                    )}

                    {subscriptionFields.upgrade && subscriptionFields.update_billing_cycle && (
                        <p style={{ fontStyle: "italic", color: "#6c757d" }}>
                            You have changed both plan and billing cycle. Do you want to proceed with renewal?
                        </p>
                    )}
                    {!subscriptionFields.upgrade && !subscriptionFields.update_billing_cycle && (
                        <p style={{ fontStyle: "italic", color: "#6c757d" }}>
                            You have not changed both plan and billing cycle. Do you want to proceed with renewal?
                        </p>
                    )}

                    {createSubscriptionError && <Alert variant="danger">{createSubscriptionError}</Alert>}
                    {createSubscriptionInfo && <Alert variant="success">{createSubscriptionInfo}</Alert>}

                    <div className="d-flex justify-content-between">
                        <Button variant="secondary" onClick={() => setShowUpgradeReviewModal(false)}>
                            Go Back & Edit
                        </Button>
                        <Button
                            variant="primary"
                            onClick={(e) => handleUpgradeSubscription(e)} // Trigger actual upgrade}
                        >
                            Confirm Upgrade
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>



            <Modal
                show={showRenewReviewModal}
                onHide={() => setShowRenewReviewModal(false)}
                centered
                backdrop="static"
                size="md"
            >
                <Modal.Header>
                    <Modal.Title>Confirm Renewal</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>You're about to renew your subscription with the following changes:</p>

                    <div className="p-3 mb-3" style={{ background: "#f8f9fa", borderRadius: "5px" }}>
                        <p>
                            <strong>Plan:</strong> {subscriptionFields.plan_change ? subscriptionFields.plan : "No change"}
                        </p>
                        <p>
                            <strong>Billing Cycle:</strong> {subscriptionFields.update_billing_cycle ? subscriptionFields.billing_cycle : "No change"}
                        </p>
                    </div>

                    {/* Optional descriptive message */}
                    {subscriptionFields.plan_change && !subscriptionFields.update_billing_cycle && (
                        <p style={{ fontStyle: "italic", color: "#6c757d" }}>
                            You have changed only the plan and not the billing cycle. Do you want to proceed with renewal?
                        </p>
                    )}

                    {subscriptionFields.update_billing_cycle && !subscriptionFields.plan_change && (
                        <p style={{ fontStyle: "italic", color: "#6c757d" }}>
                            You have changed only the billing cycle and not the plan. Do you want to proceed with renewal?
                        </p>
                    )}

                    {subscriptionFields.plan_change && subscriptionFields.update_billing_cycle && (
                        <p style={{ fontStyle: "italic", color: "#6c757d" }}>
                            You have changed both plan and billing cycle. Do you want to proceed with renewal?
                        </p>
                    )}
                    {!subscriptionFields.plan_change && !subscriptionFields.update_billing_cycle && (
                        <p style={{ fontStyle: "italic", color: "#6c757d" }}>
                            You have not changed both plan and billing cycle. Do you want to proceed with renewal?
                        </p>
                    )}

                    {createSubscriptionError && <Alert variant="danger">{createSubscriptionError}</Alert>}
                    {createSubscriptionInfo && <Alert variant="success">{createSubscriptionInfo}</Alert>}


                    <div className="d-flex justify-content-between mt-3">
                        <Button variant="secondary" onClick={() => setShowRenewReviewModal(false)}>
                            Go Back & Edit
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => handleRenewSubscription()}
                        >
                            Confirm Renewal
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

        </div>
    );
};

export default Subscriptions;